import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import PublicBrandHeader from "@/components/PublicBrandHeader";
import { useBranding } from "@/contexts/BrandingContext";

export default function ForgotPasswordPage() {
  const { sender_email } = useBranding();
  useDocumentTitle("Forgot password", "Reset your CRM password via email.");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post(`/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (err) {
      // The backend always returns 200 with a generic message — only catch network errors.
      const status = err?.response?.status;
      if (status === 429) {
        setError("Too many reset requests from your network. Please wait a few minutes.");
      } else if (status === 422) {
        setError("Please enter a valid email address.");
      } else {
        setError("Couldn't send the reset link right now. Please try again in a moment.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50" data-testid="forgot-password-page">
      <div className="w-full max-w-[420px]">
        <PublicBrandHeader subtitle="Account recovery" />
        <div className="bg-white border border-border rounded-md p-7 shadow-sm">
          <Link to="/login" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-5" data-testid="back-to-login-link">
            <ArrowLeft size={13} /> Back to sign in
          </Link>

          {!submitted ? (
            <>
              <h1 className="font-heading font-bold text-2xl tracking-tight mb-1">Forgot your password?</h1>
              <p className="text-sm text-muted-foreground mb-6">
                Enter your email and we'll send you a link to reset it. The link expires in one hour.
              </p>

              {error && (
                <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-xs font-medium">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="mt-1"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white" disabled={loading} data-testid="forgot-submit-btn">
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-success">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                  <CheckCircle2 size={24} className="text-emerald-600" />
                </div>
              </div>
              <h1 className="font-heading font-bold text-xl tracking-tight text-center mb-2">Check your email</h1>
              <p className="text-sm text-muted-foreground text-center mb-1">
                If an account exists for <strong>{email}</strong>, we've sent a password reset link.
              </p>
              <p className="text-xs text-muted-foreground text-center mb-6">
                The link expires in 1 hour. Don't see it? Check spam.
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full" data-testid="forgot-back-btn">Back to sign in</Button>
              </Link>
            </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-1 mt-4 text-[11px] text-muted-foreground">
          <Mail size={11} /> Reset emails are sent from {sender_email || "our support address"}
        </div>
      </div>
    </div>
  );
}
